import Hook from "../utils/hook"

// `SiteSaveModalComponent` (rendered once per page for signed-in users): opens its
// save modal for a site and keeps the site cards (`SiteCard`)
// marked while their site is saved somewhere. The saved site ids are asked for
// once, when the first card shows up, and kept up to date by the
// `site-save-modal:changed` events the component (and the site page) push.
// The embedded maps (`Map`) are told to reload their saved sites on those
// events, and a site saved or unsaved in a map reloads the saved site ids. A
// change reported by this hook comes back with its `origin` and isn't sent
// back to the maps, which made it (it'd reload the map's open save modal).
export default Hook(`SiteSaveModal`, {
  savedIds: null,
  savedIdsRequested: false,

  mounted() {
    this.cards = new Set()
    this.maps = new Set()
    this.origin = window.crypto.randomUUID()

    this.handleEvent(
      `site-save-modal:changed`,
      ({ site_id: siteId, saved, origin }) => {
        if (origin !== this.origin)
          this.maps.forEach((map) => map.sendSavedSitesChanged())

        if (!this.savedIds) return

        if (saved) this.savedIds.add(siteId)
        else this.savedIds.delete(siteId)

        this.cards.forEach((card) => this.markCard(card))
      }
    )
  },

  destroyed() {
    this.cards.clear()
    this.maps.clear()
  },

  addMap(map) {
    this.maps.add(map)
  },

  removeMap(map) {
    this.maps.delete(map)
  },

  // the site was saved or unsaved in a map, through the API
  mapSavedSitesChanged(siteId) {
    this.pushEventTo(
      this.el,
      `changed`,
      { site_id: siteId, origin: this.origin },
      ({ ids }) => {
        this.savedIds = new Set(ids)
        this.savedIdsRequested = true
        this.cards.forEach((card) => this.markCard(card))
      }
    )
  },

  addCard(card) {
    this.cards.add(card)

    if (this.savedIds) this.markCard(card)
    else this.requestSavedIds()
  },

  removeCard(card) {
    this.cards.delete(card)
  },

  requestSavedIds() {
    if (this.savedIdsRequested) return
    this.savedIdsRequested = true

    this.pushEventTo(this.el, `saved_ids`, {}, ({ ids }) => {
      this.savedIds = new Set(ids)
      this.cards.forEach((card) => this.markCard(card))
    })
  },

  markCard(card) {
    card.savedValue = this.savedIds.has(card.siteValue)
  },

  open(siteId) {
    this.pushEventTo(this.el, `open`, { site_id: siteId })

    // the modal is portaled out of the component into <body>
    const modal = document.getElementById(`${this.el.id}-modal`)
    if (modal)
      this.liveSocket.execJS(modal, modal.getAttribute(`data-modal-show`))
  },
})
